import React, { useMemo, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { groupProjects, slugOf } from '../lib/portfolioGroups';

// ═══════════════════════════════════════════════════════════════════════════
//  OUR WORK — the admin screen behind the public portfolio page
//
//  Julian photographs a finished job on his phone and wants it on the site
//  the same evening, without a deploy. This is that screen.
//
//  It is written for somebody standing in a driveway, not sitting at a desk:
//
//    · ONE form, top to bottom, in the order a person would say it out loud.
//      Name the job, say which portfolio it belongs in, add the photos.
//      Nothing is nested, nothing is behind a tab.
//
//    · Nothing goes live by accident. A new project starts as a draft and
//      says so, in words, on its own row. "Put it on the website" is a
//      separate, deliberate press.
//
//    · The portfolio name is typed, with the names already in use offered
//      underneath. A dropdown he cannot add to is a dropdown he works
//      around, and a free-text field with no suggestions is how one
//      portfolio becomes "Decks", "decks " and "Deck".
//
//    · The address a portfolio will have is shown while he types the name,
//      because that link is the thing he is going to text to a customer.
//
//  The ordering rules it explains on screen are implemented and tested in
//  lib/portfolioGroups.js. If they ever disagree, that file is the truth.
// ═══════════════════════════════════════════════════════════════════════════

const blank = () => ({
  id: null,
  title: '',
  portfolio: '',
  summary: '',
  location: '',
  completedOn: '',
  position: 0,
  published: false,
  photos: [],
});

// <input type="date"> wants exactly YYYY-MM-DD and silently shows nothing for
// anything else, including the ISO string the API sends back.
const dateValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

export default function WorkEditor() {
  const { workAll, saveWorkProject, deleteWorkProject, uploadPhotos } = useApp();

  const [form, setForm] = useState(null);     // null = the list, otherwise the form
  const [saving, setSaving] = useState(false);
  const [busyPhotos, setBusyPhotos] = useState(false);
  const fileInput = useRef(null);

  // Memoised, so that `groups` below is not rebuilt on every keystroke in the
  // form: a bare `workAll || []` is a new array each render and defeats it.
  const rows = useMemo(() => (Array.isArray(workAll) ? workAll : []), [workAll]);
  const live = rows.filter((r) => r.published);

  // The admin sees the same arrangement the public page draws, so what is on
  // screen here is what a visitor gets. Drafts are grouped alongside, marked.
  const groups = useMemo(() => groupProjects(rows), [rows]);

  // Every portfolio name already in use, for the suggestions under the field.
  const names = useMemo(() => {
    const seen = new Map();
    for (const g of groups) if (g.name) seen.set(g.slug, g.name);
    return [...seen.values()];
  }, [groups]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const edit = (row) => setForm({
    id: row.id,
    title: row.title || '',
    portfolio: row.portfolio || '',
    summary: row.summary || '',
    location: row.location || '',
    completedOn: dateValue(row.completedOn),
    position: typeof row.position === 'number' ? row.position : 0,
    published: !!row.published,
    photos: (row.photos || []).map((p) => ({ ...p })),
  });

  // A new project lands in the portfolio Julian was last looking at, because
  // photos arrive in batches: four decks in one sitting, not one of each.
  const add = (portfolio = '') => setForm({ ...blank(), portfolio });

  const addPhotos = async (files) => {
    const picked = Array.from(files || []);
    if (!picked.length) return;
    setBusyPhotos(true);
    try {
      const urls = await uploadPhotos(picked);
      if (urls.length) {
        setForm((f) => ({
          ...f,
          photos: [...f.photos, ...urls.map((url) => ({ url, caption: '', isCover: false }))],
        }));
      }
    } finally {
      setBusyPhotos(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const movePhoto = (i, by) => setForm((f) => {
    const next = [...f.photos];
    const j = i + by;
    if (j < 0 || j >= next.length) return f;
    [next[i], next[j]] = [next[j], next[i]];
    return { ...f, photos: next };
  });

  const dropPhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, k) => k !== i) }));

  // One cover, always. Setting a new one clears the old, rather than leaving
  // two marked and letting the page pick.
  const makeCover = (i) => setForm((f) => ({
    ...f,
    photos: f.photos.map((p, k) => ({ ...p, isCover: k === i })),
  }));

  const captionPhoto = (i, caption) => setForm((f) => ({
    ...f,
    photos: f.photos.map((p, k) => (k === i ? { ...p, caption } : p)),
  }));

  const save = async () => {
    if (!form.title.trim()) { alert('Give the project a name first.'); return; }
    setSaving(true);
    const ok = await saveWorkProject(form.id, {
      title: form.title,
      portfolio: form.portfolio,
      summary: form.summary,
      location: form.location,
      completedOn: form.completedOn || null,
      position: Number(form.position) || 0,
      published: form.published,
      photos: form.photos,
    });
    setSaving(false);
    if (ok) setForm(null);
  };

  const remove = async (row) => {
    // Named in the question, not just "this project". The row is one of
    // several on the screen and the press is not undoable.
    if (!window.confirm(`Remove "${row.title}" from the website for good? This cannot be undone.`)) return;
    await deleteWorkProject(row.id);
  };

  const togglePublish = async (row) => {
    await saveWorkProject(row.id, { published: !row.published });
  };

  // ── The form ────────────────────────────────────────────────────────────
  if (form) {
    // The same function the public page uses, not a copy of its rules. The
    // address shown here is one Julian is about to text to a customer, and a
    // second implementation is how it quietly stops matching the real one.
    const slug = slugOf(form.portfolio);
    return (
      <div className="we-form">
        <div className="we-bar">
          <button type="button" className="we-back" onClick={() => setForm(null)}>← All projects</button>
          <b>{form.id ? 'Edit project' : 'Add a project'}</b>
        </div>

        <label className="we-f">
          <span>What was the job?</span>
          <input
            type="text" value={form.title} placeholder="Basement finish in Lehi"
            onChange={(e) => set('title', e.target.value)}
          />
          <i>This is the heading a customer reads first.</i>
        </label>

        <label className="we-f">
          <span>Which portfolio?</span>
          <input
            type="text" list="we-portfolios" value={form.portfolio} placeholder="Basements"
            onChange={(e) => set('portfolio', e.target.value)}
          />
          <datalist id="we-portfolios">
            {names.map((n) => <option key={n} value={n} />)}
          </datalist>
          <i>
            {slug
              ? <>Its own page: <b>qpshub.com/work/{slug}</b> — a link you can text to anyone asking about this kind of work.</>
              : 'Decks, Basements, Kitchens. Projects sharing a name sit together on one page. Leave it empty and it goes under "Other projects".'}
          </i>
        </label>

        <label className="we-f">
          <span>Tell them about it</span>
          <textarea
            rows={4} value={form.summary} placeholder="Framed, wired and finished a 900 sq ft basement, including a bathroom and an egress window."
            onChange={(e) => set('summary', e.target.value)}
          />
          <i>A few sentences. What the job was and what it took.</i>
        </label>

        <div className="we-row">
          <label className="we-f">
            <span>City</span>
            <input
              type="text" value={form.location} placeholder="Lehi"
              onChange={(e) => set('location', e.target.value)}
            />
            {/* Said out loud, on the screen, every time. A portfolio page is
                the easiest place in this app to publish a customer's home
                address by habit. */}
            <i>The city only. Never the street address of somebody's home.</i>
          </label>

          <label className="we-f">
            <span>Finished</span>
            <input
              type="date" value={form.completedOn}
              onChange={(e) => set('completedOn', e.target.value)}
            />
            <i>Shown as the month and year.</i>
          </label>

          <label className="we-f we-f-sm">
            <span>Order</span>
            <input
              type="number" value={form.position}
              onChange={(e) => set('position', e.target.value)}
            />
            <i>Lower comes first. Use a minus number to pin it to the top.</i>
          </label>
        </div>

        {/* ── Photos ── */}
        <div className="we-photos">
          <div className="we-photos-top">
            <b>Photos</b>
            <button
              type="button" className="btn btn-secondary btn-sm"
              onClick={() => fileInput.current && fileInput.current.click()}
              disabled={busyPhotos}
            >
              {busyPhotos ? 'Uploading…' : '+ Add photos'}
            </button>
            <input
              ref={fileInput} type="file" accept="image/*" multiple
              style={{ display: 'none' }}
              onChange={(e) => addPhotos(e.target.files)}
            />
          </div>

          {form.photos.length === 0 ? (
            <p className="we-empty">No photos yet. The first one becomes the cover unless you choose another.</p>
          ) : (
            <div className="we-shots">
              {form.photos.map((p, i) => (
                <div className={`we-shot${p.isCover ? ' cover' : ''}`} key={p.url + i}>
                  <div className="we-shot-img" style={{ backgroundImage: `url(${p.url})` }} />
                  <div className="we-shot-side">
                    <input
                      type="text" value={p.caption} placeholder="What this photo shows"
                      onChange={(e) => captionPhoto(i, e.target.value)}
                    />
                    <div className="we-shot-acts">
                      <button type="button" onClick={() => movePhoto(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                      <button type="button" onClick={() => movePhoto(i, 1)} disabled={i === form.photos.length - 1} aria-label="Move down">↓</button>
                      <button type="button" className={p.isCover ? 'on' : ''} onClick={() => makeCover(i)}>
                        {p.isCover ? 'Cover' : 'Make cover'}
                      </button>
                      <button type="button" className="danger" onClick={() => dropPhoto(i)}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Live or not ── */}
        <label className="we-live">
          <input
            type="checkbox" checked={form.published}
            onChange={(e) => set('published', e.target.checked)}
          />
          <span>
            <b>Show this on the website</b>
            <i>Leave it off while you are still writing. Nobody sees it until this is ticked.</i>
          </span>
        </label>

        <div className="we-save">
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save project'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  // ── The list ────────────────────────────────────────────────────────────
  return (
    <div className="we">
      <div className="we-head">
        <div>
          <b>Our Work</b>
          <i>
            {rows.length === 0
              ? 'Nothing here yet. Add a finished job and it appears on qpshub.com/work.'
              : `${live.length} of ${rows.length} showing on qpshub.com/work`}
          </i>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => add('')}>+ Add a project</button>
      </div>

      {rows.length === 0 ? (
        <div className="we-blank">
          <p>
            A project is one finished job: a name, a few photos and a paragraph. Give several
            projects the same portfolio name and they group together on a page of their own,
            at an address you can text to a customer.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => add('')}>+ Add the first project</button>
        </div>
      ) : (
        groups.map((g) => (
          <div className="we-group" key={g.slug || 'other'}>
            <div className="we-group-top">
              <b>{g.name || 'Other projects'}</b>
              {g.slug && <code>/work/{g.slug}</code>}
              <span className="we-sp" />
              <button type="button" className="we-add" onClick={() => add(g.name)}>+ Add to this one</button>
            </div>

            {g.projects.map((row) => {
              const shots = (row.photos || []).filter((p) => p && p.url);
              const cover = shots.find((p) => p.isCover) || shots[0] || null;
              return (
                <div className={`we-row-item${row.published ? '' : ' draft'}`} key={row.id}>
                  <div
                    className="we-thumb"
                    style={cover ? { backgroundImage: `url(${cover.url})` } : undefined}
                  >
                    {!cover && <span>no photo</span>}
                  </div>

                  <div className="we-item-body">
                    <b>{row.title}</b>
                    <i>
                      {[
                        row.location,
                        shots.length === 1 ? '1 photo' : `${shots.length} photos`,
                        `order ${row.position}`,
                      ].filter(Boolean).join(' · ')}
                    </i>
                    <span className={`we-state${row.published ? ' live' : ''}`}>
                      {row.published ? 'On the website' : 'Draft — nobody can see this'}
                    </span>
                  </div>

                  <div className="we-item-acts">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => edit(row)}>Edit</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => togglePublish(row)}>
                      {row.published ? 'Take it down' : 'Put it up'}
                    </button>
                    <button type="button" className="we-del" onClick={() => remove(row)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}

      {rows.length > 0 && (
        <p className="we-foot">
          Portfolios appear on the page in the order of their best-placed project, so to move a
          portfolio up, lower the Order number on one of its projects.
        </p>
      )}
    </div>
  );
}
